// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

/**
 * Turning a parsed BuildingConnected sheet into this project's records.
 *
 * Most GCs who would use this app already solicit bids somewhere else, and
 * BuildingConnected is where. Re-keying a leveling sheet is the reason a
 * package never gets into the estimate at all.
 *
 * ── Why the mapping is what it is ───────────────────────────────────────────
 *
 * This project already had the right model, and it is not the one a guess
 * would have produced. A leveled total here is:
 *
 *     bid total  +  every adjustment the bidder did NOT include
 *
 * So the two halves of BC's grid land in two different places:
 *
 *   - A cell the bidder priced themselves becomes a line on their Bid.
 *   - A cell the estimator plugged or corrected becomes a LevelingAdjustment
 *     marked not-included, carrying the plug amount.
 *
 * Added back together that is exactly BC's leveled column, and taken apart it
 * is the thing BC's own export will not tell you: how much of a bidder's
 * comparison figure is their price and how much is yours.
 *
 * On the reference file this reproduced BuildingConnected's printed Base Bid
 * exactly for four of five bidders. The fifth had two rows CORRECTED rather
 * than plugged, and a correction overwrites the bidder's figure in place --
 * the original is nowhere in the export. That is reported rather than
 * papered over, because the alternative is a bid total that quietly disagrees
 * with the PDF the estimator has open beside it.
 *
 * ── The one rule ────────────────────────────────────────────────────────────
 *
 * Never invent. A bidder whose name does not match an existing partner
 * becomes a NEW partner the user can see and re-point, not a guess at which
 * existing one was meant. Same rule the estimate importer follows for cost
 * codes, for the same reason: an unplaced row is visible and fixable, and a
 * wrongly-placed one looks correct and is wrong everywhere it is counted.
 */

import type { BidScope, BidPartner, Bid, BidItem, LevelingAdjustment } from "../projectData";
import type { BcLevelingSheet } from "./bcLeveling";
import { COMPARISON_SECTION, bcBidderTotals } from "./bcLeveling";

const norm = (s: string) =>
  (s || "").toLowerCase().replace(/[.,'"()]/g, "").replace(/\b(inc|llc|co|corp|ltd|company)\b/g, "")
    .replace(/\s+/g, " ").trim();

export interface NameMatch<T> {
  /** The name as the sheet spelled it. */
  incoming: string;
  /** The existing record this lands on, or null when it is new. */
  existing: T | null;
}

/**
 * Line up incoming names with what the project already has.
 *
 * Exact after normalisation, and nothing else. Company suffixes and
 * punctuation are stripped because "Acme Electric, Inc." and "Acme Electric"
 * are the same firm and nobody would call that a guess; anything beyond that
 * IS a guess, and a mis-pointed bidder silently moves money between two real
 * companies.
 */
export function matchNames<T extends { name: string }>(
  incoming: string[], existing: T[],
): Array<NameMatch<T>> {
  const byNorm = new Map<string, T>();
  for (const e of existing || []) {
    const k = norm(e.name);
    if (k && !byNorm.has(k)) byNorm.set(k, e);
  }
  return (incoming || []).map(name => ({ incoming: name, existing: byNorm.get(norm(name)) || null }));
}

/** The package this sheet belongs to, if the project already has it. */
export const matchScope = (scopeName: string, scopes: BidScope[]): BidScope | null =>
  matchNames([scopeName], scopes || [])[0]?.existing ?? null;

/** One bidder's column, as the review step shows it. */
export interface BidderReview {
  bidder: string;
  /** Matched existing partner, or null when this will be created. */
  existing: BidPartner | null;
  /** What BuildingConnected printed for this bidder. */
  stated: number | null;
  /** Lines the bidder priced themselves -- becomes their Bid. */
  own: number;
  /** Plugs and corrections -- become leveling adjustments. */
  plugged: number;
  /** own + plugged: what this bidder is actually being compared at. */
  leveled: number;
  /**
   * Set when `own` cannot be reconciled to the printed figure, which happens
   * when a row was corrected in place and the original is not in the file.
   */
  ownDiffersFromStated: boolean;
}

export interface BcImportReview {
  packageName: string;
  matchedScope: BidScope | null;
  bidders: BidderReview[];
  /** Priced rows that will become bid lines. */
  lineCount: number;
  /** Rows that will become leveling adjustments. */
  adjustmentCount: number;
  /** Sections present in the file that this import does not read. */
  skippedSections: string[];
  warnings: string[];
}

/** Rows that carry a per-bidder yes/no plus a plug when the answer is no. */
const ADJUSTMENT_SECTIONS = ["INCLUSIONS"];

export function reviewBcImport(
  sheet: BcLevelingSheet, scopes: BidScope[], partners: BidPartner[],
): BcImportReview {
  const totals = bcBidderTotals(sheet, COMPARISON_SECTION);
  const matches = matchNames(sheet.bidders.map(b => b.name), partners || []);
  const adjRows = sheet.rows.filter(r => ADJUSTMENT_SECTIONS.includes(r.section) && !r.isGroupHeader);

  const bidders: BidderReview[] = sheet.bidders.map((b, i) => {
    const t = totals[i];
    const incPlugs = adjRows.reduce((a, r) => a + (r.cells[i]?.amount ?? 0), 0);
    return {
      bidder: b.name,
      existing: matches[i]?.existing ?? null,
      stated: b.statedTotal,
      own: t.own,
      plugged: t.plugged + incPlugs,
      leveled: t.own + t.plugged + incPlugs,
      ownDiffersFromStated: b.statedTotal != null && Math.abs(t.own - b.statedTotal) > 0.5,
    };
  });

  const warnings = [...sheet.warnings];
  for (const b of bidders) {
    if (!b.ownDiffersFromStated) continue;
    warnings.push(
      `${b.bidder}: their priced lines add to ${Math.round(b.own).toLocaleString()}, ` +
      `but BuildingConnected printed ${Math.round(b.stated!).toLocaleString()}. ` +
      `A corrected cell replaces the bidder's figure in place, so the original is not in the export.`);
  }

  const known = [COMPARISON_SECTION, ...ADJUSTMENT_SECTIONS];
  const skippedSections = [...new Set(
    sheet.rows.filter(r => !known.includes(r.section) && r.cells.some(c => c.amount != null))
      .map(r => r.section))];

  return {
    packageName: sheet.packageName,
    matchedScope: matchScope(sheet.packageName, scopes),
    bidders,
    lineCount: sheet.rows.filter(r => r.section === COMPARISON_SECTION && !r.isGroupHeader).length,
    adjustmentCount: adjRows.length,
    skippedSections,
    warnings,
  };
}

export interface BcImportPlan {
  scope: BidScope;
  /** Only the partners that do not exist yet. */
  newPartners: BidPartner[];
  bids: Bid[];
  adjustments: LevelingAdjustment[];
}

/**
 * The records to write, built after the user has confirmed every match.
 *
 * `partnerIds` is index-aligned with the sheet's bidders and comes from the
 * review step -- an existing partner's id, or a freshly minted one.
 */
export function buildBcImportPlan(
  sheet: BcLevelingSheet,
  scope: BidScope,
  partnerIds: string[],
  partnerNames: string[],
  existingPartners: BidPartner[],
  uid: () => string,
  today: string,
): BcImportPlan {
  const have = new Set((existingPartners || []).map(p => p.id));
  const newPartners: BidPartner[] = [];
  partnerIds.forEach((pid, i) => {
    if (have.has(pid)) return;
    newPartners.push({
      id: pid, name: partnerNames[i] || sheet.bidders[i]?.name || "Unnamed bidder",
      trade: scope.name || "", contact: sheet.bidders[i]?.submittedBy || "",
      notes: "Imported from BuildingConnected",
    });
  });

  const lineRows = sheet.rows.filter(r => r.section === COMPARISON_SECTION && !r.isGroupHeader);
  const adjRows = sheet.rows.filter(r => ADJUSTMENT_SECTIONS.includes(r.section) && !r.isGroupHeader);

  const bids: Bid[] = partnerIds.map((pid, i) => {
    const items: BidItem[] = [];
    for (const row of lineRows) {
      const c = row.cells[i];
      // A plugged or corrected cell is not this bidder's price; it becomes an
      // adjustment below, so that the two never get added twice.
      if (!c || c.amount == null || c.adjusted) continue;
      items.push({
        id: uid(),
        description: row.group ? `${row.group} — ${row.label}` : row.label,
        qty: 1, unit: "LS", unitCost: c.amount,
        notes: c.note || "",
      });
    }
    const b = sheet.bidders[i];
    const statedNote = b?.statedTotal != null
      ? `BuildingConnected printed ${Math.round(b.statedTotal).toLocaleString()}`
      : "";
    return {
      id: uid(), partnerId: pid, scopeId: scope.id, version: 1, date: today,
      status: "Active" as const, items, pdfName: "",
      notes: [b?.proposalRef, statedNote].filter(Boolean).join(" · "),
    };
  });

  // Every row that carries a plug for anybody, line items and inclusions
  // alike. `included: false` is what makes this count toward the leveled
  // total in this project's model.
  const adjustments: LevelingAdjustment[] = [];
  for (const row of [...lineRows, ...adjRows]) {
    const partners: LevelingAdjustment["partners"] = {};
    let any = false;
    row.cells.forEach((c, i) => {
      const pid = partnerIds[i];
      if (!pid || !c) return;
      const isPlug = c.amount != null && (c.adjusted || adjRows.includes(row));
      if (!isPlug) return;
      partners[pid] = { included: false, amount: c.amount ?? 0 };
      any = true;
    });
    if (!any) continue;
    adjustments.push({
      id: uid(),
      scopeId: scope.id,
      description: row.group ? `${row.group} — ${row.label}` : row.label,
      partners,
    });
  }

  return { scope, newPartners, bids, adjustments };
}
